// Insurance country + insurer catalogs, keyed by country.
// Extracted from Step4Insurance so catalogs can be maintained (and later
// moved into per-org config) without touching component logic.
import type { InsuranceType } from '../types'

// Countries for insurance (ISO code mapping for Intl.DisplayNames)
export const INSURANCE_COUNTRY_ISO: Record<string, string> = {
  Germany: 'DE', Austria: 'AT', Switzerland: 'CH', Spain: 'ES', Italy: 'IT',
}

export function buildInsuranceCountries(locale: string, otherLabel: string): { value: string; label: string }[] {
  let displayNames: Intl.DisplayNames
  try {
    displayNames = new Intl.DisplayNames([locale], { type: 'region' })
  } catch {
    displayNames = new Intl.DisplayNames(['en'], { type: 'region' })
  }
  const entries = Object.entries(INSURANCE_COUNTRY_ISO).map(([value, iso]) => ({
    value,
    label: displayNames.of(iso) || value,
  }))
  return [...entries, { value: 'Other', label: otherLabel }]
}

// German public health insurances (Gesetzliche Krankenkassen)
export const GERMAN_PUBLIC_INSURERS = [
  { value: '', label: 'Bitte wählen...' },
  { value: 'AOK', label: 'AOK' },
  // Regional AOKs and the statutory insurers below 'R+V BKK': the booking
  // site's intake offers them, this list did not (a Bahn-BKK patient had to
  // write in afterwards, 23.10.2026).
  { value: 'AOK Baden-Württemberg', label: 'AOK Baden-Württemberg' },
  { value: 'AOK Bayern', label: 'AOK Bayern' },
  { value: 'AOK Bremen/Bremerhaven', label: 'AOK Bremen/Bremerhaven' },
  { value: 'AOK Hessen', label: 'AOK Hessen' },
  { value: 'AOK Niedersachsen', label: 'AOK Niedersachsen' },
  { value: 'AOK NordWest', label: 'AOK NordWest' },
  { value: 'AOK Nordost', label: 'AOK Nordost' },
  { value: 'AOK PLUS', label: 'AOK PLUS' },
  { value: 'AOK Rheinland/Hamburg', label: 'AOK Rheinland/Hamburg' },
  { value: 'AOK Rheinland-Pfalz/Saarland', label: 'AOK Rheinland-Pfalz/Saarland' },
  { value: 'AOK Sachsen-Anhalt', label: 'AOK Sachsen-Anhalt' },
  { value: 'Techniker Krankenkasse', label: 'Techniker Krankenkasse (TK)' },
  { value: 'BARMER', label: 'BARMER' },
  { value: 'DAK-Gesundheit', label: 'DAK-Gesundheit' },
  { value: 'IKK classic', label: 'IKK classic' },
  { value: 'KKH', label: 'KKH Kaufmännische Krankenkasse' },
  { value: 'HEK', label: 'HEK - Hanseatische Krankenkasse' },
  { value: 'hkk', label: 'hkk Krankenkasse' },
  { value: 'Knappschaft', label: 'Knappschaft' },
  { value: 'IKK gesund plus', label: 'IKK gesund plus' },
  { value: 'IKK Südwest', label: 'IKK Südwest' },
  { value: 'BIG direkt', label: 'BIG direkt gesund' },
  { value: 'Mobil Krankenkasse', label: 'Mobil Krankenkasse' },
  { value: 'Pronova BKK', label: 'Pronova BKK' },
  { value: 'Viactiv', label: 'Viactiv Krankenkasse' },
  { value: 'SBK', label: 'SBK Siemens-Betriebskrankenkasse' },
  { value: 'Audi BKK', label: 'Audi BKK' },
  { value: 'BMW BKK', label: 'BMW BKK' },
  { value: 'Bosch BKK', label: 'Bosch BKK' },
  { value: 'Daimler BKK', label: 'Daimler BKK' },
  { value: 'mhplus BKK', label: 'mhplus BKK' },
  { value: 'Novitas BKK', label: 'Novitas BKK' },
  { value: 'Salus BKK', label: 'Salus BKK' },
  { value: 'vivida bkk', label: 'vivida bkk' },
  { value: 'BKK Linde', label: 'BKK Linde' },
  { value: 'BKK firmus', label: 'BKK firmus' },
  { value: 'BKK VBU', label: 'BKK VBU' },
  { value: 'energie-BKK', label: 'energie-BKK' },
  { value: 'BKK Pfalz', label: 'BKK Pfalz' },
  { value: 'Heimat Krankenkasse', label: 'Heimat Krankenkasse' },
  { value: 'BKK24', label: 'BKK24' },
  { value: 'BKK Melitta Plus', label: 'BKK Melitta Plus' },
  { value: 'BKK Euregio', label: 'BKK Euregio' },
  { value: 'BKK Freudenberg', label: 'BKK Freudenberg' },
  { value: 'BKK Gildemeister Seidensticker', label: 'BKK Gildemeister Seidensticker' },
  { value: 'BKK Herkules', label: 'BKK Herkules' },
  { value: 'BKK Technoform', label: 'BKK Technoform' },
  { value: 'BKK Textilgruppe Hof', label: 'BKK Textilgruppe Hof' },
  { value: 'BKK Wirtschaft & Finanzen', label: 'BKK Wirtschaft & Finanzen' },
  { value: 'BKK ZF & Partner', label: 'BKK ZF & Partner' },
  { value: 'WMF BKK', label: 'WMF BKK' },
  { value: 'Bertelsmann BKK', label: 'Bertelsmann BKK' },
  { value: 'Continentale BKK', label: 'Continentale BKK' },
  { value: 'Debeka BKK', label: 'Debeka BKK' },
  { value: 'R+V BKK', label: 'R+V BKK' },
  { value: 'actimonda BKK', label: 'actimonda BKK' },
  { value: 'atene BKK', label: 'atene BKK' },
  { value: 'Bahn-BKK', label: 'Bahn-BKK' },
  { value: 'Bitburger BKK', label: 'Bitburger BKK' },
  { value: 'BKK Achenbach Buschhütten', label: 'BKK Achenbach Buschhütten' },
  { value: 'BKK Akzo Nobel Bayern', label: 'BKK Akzo Nobel Bayern' },
  { value: 'BKK B. Braun Aesculap', label: 'BKK B. Braun Aesculap' },
  { value: 'BKK Deutsche Bank', label: 'BKK Deutsche Bank' },
  { value: 'BKK Diakonie', label: 'BKK Diakonie' },
  { value: 'BKK Dürkopp Adler', label: 'BKK Dürkopp Adler' },
  { value: 'BKK EWE', label: 'BKK EWE' },
  { value: 'BKK exklusiv', label: 'BKK exklusiv' },
  { value: 'BKK Faber-Castell & Partner', label: 'BKK Faber-Castell & Partner' },
  { value: 'BKK Groz-Beckert', label: 'BKK Groz-Beckert' },
  { value: 'BKK HBH', label: 'BKK HBH' },
  { value: 'BKK Karl Mayer', label: 'BKK Karl Mayer' },
  { value: 'BKK Mahle', label: 'BKK Mahle' },
  { value: 'BKK MEM', label: 'BKK MEM' },
  { value: 'BKK Miele', label: 'BKK Miele' },
  { value: 'BKK MTU', label: 'BKK MTU' },
  { value: 'BKK Pfaff', label: 'BKK Pfaff' },
  { value: 'BKK ProVita', label: 'BKK ProVita' },
  { value: 'BKK Public', label: 'BKK Public' },
  { value: 'BKK PwC', label: 'BKK PwC' },
  { value: 'BKK Rieker Ricosta Weisser', label: 'BKK Rieker Ricosta Weisser' },
  { value: 'BKK RWE', label: 'BKK RWE' },
  { value: 'BKK Salzgitter', label: 'BKK Salzgitter' },
  { value: 'BKK Scheufelen', label: 'BKK Scheufelen' },
  { value: 'BKK Schwarzwald-Baar-Heuberg', label: 'BKK Schwarzwald-Baar-Heuberg' },
  { value: 'BKK Stadt Augsburg', label: 'BKK Stadt Augsburg' },
  { value: 'BKK VDN', label: 'BKK VDN' },
  { value: 'BKK Verbund Plus', label: 'BKK Verbund Plus' },
  { value: 'BKK Voralb', label: 'BKK Voralb' },
  { value: 'BKK Werra-Meissner', label: 'BKK Werra-Meissner' },
  { value: 'DEVK BKK', label: 'DEVK BKK' },
  { value: 'Die Bergische Krankenkasse', label: 'Die Bergische Krankenkasse' },
  { value: 'Die Schwenninger Krankenkasse', label: 'Die Schwenninger Krankenkasse' },
  { value: 'Gemeinsame Betriebskrankenkasse Thüringen', label: 'Gemeinsame Betriebskrankenkasse Thüringen' },
  { value: 'IKK Brandenburg und Berlin', label: 'IKK Brandenburg und Berlin' },
  { value: 'Koenig & Bauer BKK', label: 'Koenig & Bauer BKK' },
  { value: 'Krones BKK', label: 'Krones BKK' },
  { value: 'Metzinger BKK', label: 'Metzinger BKK' },
  { value: 'mkk – meine krankenkasse', label: 'mkk – meine krankenkasse' },
  { value: 'SECURVITA BKK', label: 'SECURVITA BKK' },
  { value: 'SKD BKK', label: 'SKD BKK' },
  { value: 'Südzucker BKK', label: 'Südzucker BKK' },
  { value: 'TUI BKK', label: 'TUI BKK' },
  { value: 'Würth BKK', label: 'Würth BKK' },
  { value: 'Other', label: 'Andere Krankenkasse' },
]

// German private health insurances (Private Krankenversicherungen)
export const GERMAN_PRIVATE_INSURERS = [
  { value: '', label: 'Bitte wählen...' },
  { value: 'Allianz Private', label: 'Allianz Private Krankenversicherung' },
  { value: 'AXA', label: 'AXA Krankenversicherung' },
  { value: 'Barmenia', label: 'Barmenia Krankenversicherung' },
  { value: 'Bayerische Beamtenkrankenkasse', label: 'Bayerische Beamtenkrankenkasse' },
  { value: 'Central', label: 'Central Krankenversicherung' },
  { value: 'Concordia', label: 'Concordia Krankenversicherung' },
  { value: 'Continentale', label: 'Continentale Krankenversicherung' },
  { value: 'DBV', label: 'DBV Deutsche Beamtenversicherung' },
  { value: 'Debeka', label: 'Debeka Krankenversicherung' },
  { value: 'Deutscher Ring', label: 'Deutscher Ring Krankenversicherung' },
  { value: 'DKV', label: 'DKV Deutsche Krankenversicherung' },
  { value: 'Envivas', label: 'Envivas Krankenversicherung' },
  { value: 'ERGO', label: 'ERGO Krankenversicherung' },
  { value: 'Generali', label: 'Generali Deutschland Krankenversicherung' },
  { value: 'Gothaer', label: 'Gothaer Krankenversicherung' },
  { value: 'Hallesche', label: 'Hallesche Krankenversicherung' },
  { value: 'HanseMerkur', label: 'HanseMerkur Krankenversicherung' },
  { value: 'HUK-COBURG', label: 'HUK-COBURG Krankenversicherung' },
  { value: 'Inter', label: 'Inter Krankenversicherung' },
  { value: 'LVM', label: 'LVM Krankenversicherung' },
  { value: 'Mecklenburgische', label: 'Mecklenburgische Krankenversicherung' },
  { value: 'Münchener Verein', label: 'Münchener Verein Krankenversicherung' },
  { value: 'Nürnberger', label: 'Nürnberger Krankenversicherung' },
  { value: 'Ottonova', label: 'Ottonova Krankenversicherung' },
  { value: 'Provinzial', label: 'Provinzial Krankenversicherung' },
  { value: 'R+V', label: 'R+V Krankenversicherung' },
  { value: 'SDK', label: 'SDK Süddeutsche Krankenversicherung' },
  { value: 'Signal Iduna', label: 'Signal Iduna Krankenversicherung' },
  { value: 'uniVersa', label: 'uniVersa Krankenversicherung' },
  { value: 'Versicherungskammer Bayern', label: 'Versicherungskammer Bayern' },
  { value: 'Württembergische', label: 'Württembergische Krankenversicherung' },
  { value: 'Other', label: 'Andere Versicherung' },
]

// Austrian public health insurances
export const AUSTRIAN_PUBLIC_INSURERS = [
  { value: '', label: 'Bitte wählen...' },
  { value: 'ÖGK', label: 'Österreichische Gesundheitskasse (ÖGK)' },
  { value: 'BVAEB', label: 'BVAEB - Versicherungsanstalt öffentlich Bediensteter' },
  { value: 'SVS', label: 'SVS - Sozialversicherung der Selbständigen' },
  { value: 'KFA', label: 'Krankenfürsorgeanstalt der Bediensteten der Stadt Wien' },
  { value: 'Other', label: 'Andere Krankenkasse' },
]

// Austrian private health insurances
export const AUSTRIAN_PRIVATE_INSURERS = [
  { value: '', label: 'Bitte wählen...' },
  { value: 'Allianz', label: 'Allianz Österreich' },
  { value: 'Generali', label: 'Generali Versicherung' },
  { value: 'Uniqa', label: 'UNIQA Österreich' },
  { value: 'Wiener Städtische', label: 'Wiener Städtische' },
  { value: 'Merkur', label: 'Merkur Versicherung' },
  { value: 'Donau', label: 'Donau Versicherung' },
  { value: 'Grawe', label: 'Grazer Wechselseitige' },
  { value: 'Helvetia', label: 'Helvetia Versicherungen' },
  { value: 'Wüstenrot', label: 'Wüstenrot Versicherung' },
  { value: 'Other', label: 'Andere Versicherung' },
]

// Swiss health insurances (all mandatory basic insurance)
export const SWISS_PUBLIC_INSURERS = [
  { value: '', label: 'Bitte wählen...' },
  { value: 'CSS', label: 'CSS Krankenversicherung' },
  { value: 'Helsana', label: 'Helsana' },
  { value: 'Swica', label: 'SWICA' },
  { value: 'Sanitas', label: 'Sanitas' },
  { value: 'Concordia', label: 'Concordia' },
  { value: 'Visana', label: 'Visana' },
  { value: 'Groupe Mutuel', label: 'Groupe Mutuel' },
  { value: 'Assura', label: 'Assura' },
  { value: 'KPT', label: 'KPT/CPT' },
  { value: 'Atupri', label: 'Atupri' },
  { value: 'EGK', label: 'EGK-Gesundheitskasse' },
  { value: 'Sympany', label: 'Sympany' },
  { value: 'ÖKK', label: 'ÖKK' },
  { value: 'Agrisano', label: 'Agrisano' },
  { value: 'Aquilana', label: 'Aquilana' },
  { value: 'Avenir', label: 'Avenir Krankenversicherung' },
  { value: 'Compact', label: 'Compact Grundversicherungen' },
  { value: 'Easy Sana', label: 'Easy Sana' },
  { value: 'Innova', label: 'INNOVA' },
  { value: 'Intras', label: 'Intras' },
  { value: 'Kolping', label: 'Kolping Krankenkasse' },
  { value: 'Luzerner Hinterland', label: 'Luzerner Hinterland' },
  { value: 'Mutuel', label: 'Mutuel Assurance' },
  { value: 'Philos', label: 'Philos' },
  { value: 'Progrès', label: 'Progrès Krankenversicherung' },
  { value: 'Provita', label: 'PROVITA' },
  { value: 'rhenusana', label: 'rhenusana' },
  { value: 'Sana24', label: 'Sana24' },
  { value: 'Sanagate', label: 'Sanagate' },
  { value: 'Sanavals', label: 'Sanavals' },
  { value: 'Sodalis', label: 'Sodalis Gesundheitsgruppe' },
  { value: 'Steffisburg', label: 'Steffisburg Krankenversicherung' },
  { value: 'Sumiswalder', label: 'Sumiswalder Krankenkasse' },
  { value: 'vita surselva', label: 'vita surselva' },
  { value: 'Vivao', label: 'Vivao Sympany' },
  { value: 'Other', label: 'Andere Krankenkasse' },
]

// Swiss private/supplementary insurances
export const SWISS_PRIVATE_INSURERS = [
  { value: '', label: 'Bitte wählen...' },
  { value: 'CSS', label: 'CSS Versicherung' },
  { value: 'Helsana', label: 'Helsana' },
  { value: 'Swica', label: 'SWICA' },
  { value: 'Sanitas', label: 'Sanitas' },
  { value: 'Visana', label: 'Visana' },
  { value: 'AXA', label: 'AXA Winterthur' },
  { value: 'Zurich', label: 'Zurich' },
  { value: 'Generali', label: 'Generali' },
  { value: 'Allianz', label: 'Allianz Suisse' },
  { value: 'Baloise', label: 'Baloise' },
  { value: 'Helvetia', label: 'Helvetia' },
  { value: 'Vaudoise', label: 'Vaudoise' },
  { value: 'Mobiliar', label: 'Die Mobiliar' },
  { value: 'Other', label: 'Andere Versicherung' },
]

// Spanish insurers
export const SPANISH_PUBLIC_INSURERS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'Seguridad Social', label: 'Seguridad Social (SNS)' },
  { value: 'MUFACE', label: 'MUFACE' },
  { value: 'MUGEJU', label: 'MUGEJU' },
  { value: 'ISFAS', label: 'ISFAS' },
  { value: 'Other', label: 'Otro seguro' },
]

export const SPANISH_PRIVATE_INSURERS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'Sanitas', label: 'Sanitas' },
  { value: 'Adeslas', label: 'SegurCaixa Adeslas' },
  { value: 'Asisa', label: 'Asisa' },
  { value: 'DKV', label: 'DKV Seguros' },
  { value: 'Mapfre Salud', label: 'Mapfre Salud' },
  { value: 'AXA', label: 'AXA Seguros' },
  { value: 'Cigna', label: 'Cigna España' },
  { value: 'Generali', label: 'Generali España' },
  { value: 'Allianz', label: 'Allianz Seguros' },
  { value: 'Caser', label: 'Caser Seguros' },
  { value: 'IMQ', label: 'IMQ' },
  { value: 'Other', label: 'Otro seguro' },
]

// Italian insurers
export const ITALIAN_PUBLIC_INSURERS = [
  { value: '', label: 'Selezionare...' },
  { value: 'SSN', label: 'Servizio Sanitario Nazionale (SSN)' },
  { value: 'Other', label: 'Altra assicurazione' },
]

export const ITALIAN_PRIVATE_INSURERS = [
  { value: '', label: 'Selezionare...' },
  { value: 'UniSalute', label: 'UniSalute' },
  { value: 'Generali', label: 'Generali Italia' },
  { value: 'Intesa Sanpaolo RBM', label: 'Intesa Sanpaolo RBM Salute' },
  { value: 'Allianz', label: 'Allianz Italia' },
  { value: 'AXA', label: 'AXA Italia' },
  { value: 'Blue Assistance', label: 'Blue Assistance' },
  { value: 'Previmedical', label: 'Previmedical' },
  { value: 'Cattolica', label: 'Cattolica Assicurazioni' },
  { value: 'Reale Mutua', label: 'Reale Mutua' },
  { value: 'Other', label: 'Altra assicurazione' },
]

// Get insurers based on country and type
export function getInsurers(country: string, type: InsuranceType | ''): { value: string; label: string }[] {
  if (!type) return [{ value: '', label: 'Bitte wählen...' }]

  switch (country) {
    case 'Germany':
      return type === 'public' ? GERMAN_PUBLIC_INSURERS : GERMAN_PRIVATE_INSURERS
    case 'Austria':
      return type === 'public' ? AUSTRIAN_PUBLIC_INSURERS : AUSTRIAN_PRIVATE_INSURERS
    case 'Switzerland':
      return type === 'public' ? SWISS_PUBLIC_INSURERS : SWISS_PRIVATE_INSURERS
    case 'Spain':
      return type === 'public' ? SPANISH_PUBLIC_INSURERS : SPANISH_PRIVATE_INSURERS
    case 'Italy':
      return type === 'public' ? ITALIAN_PUBLIC_INSURERS : ITALIAN_PRIVATE_INSURERS
    default:
      return [
        { value: '', label: 'Bitte wählen...' },
        { value: 'Other', label: 'Andere Versicherung' },
      ]
  }
}
